import * as Sentry from "@sentry/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { EducationHubExplore } from "../components/education/EducationHubExplore";
import { Header } from "../components/Header";
import { TaskCard } from "../components/journey/CurrentTasks/TaskCard";
import {
	TaskTabsToggle,
	type TaskTabValue,
} from "../components/journey/CurrentTasks/TaskTabsToggle";
import {
	TaskViewToggle,
	type TaskViewValue,
} from "../components/journey/CurrentTasks/TaskViewToggle";
import { GetReadyStepSection } from "../components/journey/GetReadyStepSection";
import { GetReadySubStepSection } from "../components/journey/GetReadySubStepSection";
import { JourneyNotAvailable } from "../components/journey/JourneyNotAvailable";
import { CustomTaskModal } from "../components/tasks/CustomTaskModal";
import { JourneyTaskDetailSheet } from "../components/track/JourneyTaskDetailSheet/JourneyTaskDetailSheet";
import { useChatActions } from "../contexts/chat-actions-context";
import { adaptJourneyProgressToSubStepSections } from "../domain/getReadyTasks/journeyProgressAdapter";
import { getTaskRoute } from "../domain/getReadyTasks/taskRoutes";
import type { GetReadyTaskRenderModel } from "../domain/getReadyTasks/types";
import { useJourneyProgress } from "../hooks/useJourneyProgress";
import { getUserDisplayName, useUser } from "../hooks/useUser";
import { useUserType } from "../hooks/useUserType";
import { useAnalytics } from "../lib/analytics";
import { useClientTranslations } from "../lib/i18n/client";
import { buildAskEvePrefill } from "../lib/journey/askEvePrefill";
import {
	deleteCustomTask,
	getCustomTasks,
	updateCustomTask,
} from "../services/customTaskService";
import type { JourneyTask, TaskCategory } from "../types/journey";
import { resolveJourneyType } from "../types/journey";
import type { GetReadyCustomTaskInstance } from "../types/task";
import styles from "./GetReadyPage.module.css";

const CUSTOM_TASK_TYPE_ICON: Record<string, string> = {
	questionnaire: "fill",
	monitor: "monitor",
	sign: "review-sign",
	book: "book",
	upload_doc: "upload",
	test: "test",
	notify: "notify",
};

function formatDeadline(
	deadline: string | null | undefined,
	locale: string,
): string | undefined {
	if (!deadline) return undefined;
	const d = new Date(deadline);
	if (Number.isNaN(d.getTime())) return deadline;
	return new Intl.DateTimeFormat(locale, {
		month: "short",
		day: "numeric",
		year: "numeric",
	}).format(d);
}

function customTaskToRenderModel(
	task: GetReadyCustomTaskInstance,
	index: number,
	locale: string,
): GetReadyTaskRenderModel {
	const taskType =
		typeof task.meta?.taskType === "string" ? task.meta.taskType : "";
	return {
		id: task.id,
		kind: "custom",
		taskKey: "user_created",
		status: task.status,
		order: task.order ?? index,
		deadlineText: formatDeadline(task.deadline, locale),
		title: task.title,
		subtext: task.description ?? undefined,
		iconName: CUSTOM_TASK_TYPE_ICON[taskType] ?? "fill",
	};
}

function LoadingSkeleton() {
	return (
		<div className={styles.skeletonContainer} aria-busy="true">
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
			<div className={styles.skeletonBlock} />
		</div>
	);
}

function ErrorState({
	onRetry,
	t,
}: {
	onRetry: () => void;
	t: (key: string, options?: { defaultValue?: string }) => string;
}) {
	return (
		<div className={styles.errorState} role="alert">
			<i className={`pi pi-exclamation-circle ${styles.errorIcon}`} />
			<h2 className={styles.errorTitle}>{t("error.title")}</h2>
			<p className={styles.errorDescription}>{t("error.description")}</p>
			<button type="button" className={styles.errorRetry} onClick={onRetry}>
				{t("error.retry")}
			</button>
		</div>
	);
}

const GET_READY_CATEGORY_MAP: Record<string, TaskCategory> = {
	admin: "clinical",
	test_appt: "clinical",
	cycle: "cycle",
	meds: "medication",
};

function renderModelToJourneyTask(task: GetReadyTaskRenderModel): JourneyTask {
	const category: TaskCategory =
		GET_READY_CATEGORY_MAP[task.category ?? ""] ?? "clinical";
	return {
		id: task.id,
		title: task.title,
		// Prefer the full CDN "about" text; fall back to the short subtext. This
		// is what the detail sheet renders under "About" (86ey1tzqx).
		description: task.description ?? task.subtext ?? "",
		sevenWordIntro: task.subtext,
		prepTips: task.prepTips,
		category,
		deadline: task.deadlineText ?? "",
		status: task.status === "completed" ? "completed" : "ongoing",
		source: "patient",
		iconName: task.iconName,
		taskDomain: task.taskDomain,
		progress: task.progress?.percent,
		progressTotal: task.progress?.total,
		progressUnit: task.progress?.unit,
	};
}

export function GetReadyPage() {
	const { t, locale } = useClientTranslations("getReady");
	const { logGetReady } = useAnalytics();
	const { data: userData } = useUser();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { setPrefillQuestion } = useChatActions();
	const { isD2C } = useUserType();
	const [infoTaskId, setInfoTaskId] = useState<string | null>(null);

	// Controls state — local only, not persisted
	const [activeTab, setActiveTab] = useState<TaskTabValue>("ongoing");
	const [viewToggle, setViewToggle] = useState<TaskViewValue>("grid"); // "grid" = expanded (default)

	// Custom-task modal state — null = closed; { editing: null } = create mode;
	// { editing: GetReadyCustomTaskInstance } = edit mode.
	const [modalState, setModalState] = useState<{
		editing: GetReadyCustomTaskInstance | null;
	} | null>(null);

	const handleTabChange = useCallback(
		(next: TaskTabValue) => {
			setActiveTab(next);
			try {
				logGetReady("tab_changed", { tab: next });
			} catch {
				// non-blocking
			}
		},
		[logGetReady],
	);

	const handleViewChange = useCallback(
		(next: TaskViewValue) => {
			setViewToggle(next);
			try {
				logGetReady("view_changed", { view: next });
			} catch {
				// non-blocking
			}
		},
		[logGetReady],
	);

	const buildOnNavigate = useCallback(
		(task: { id: string; taskKey: string; actionCategory?: string }) => {
			const route = getTaskRoute(task);
			if (route) {
				return () => navigate(route);
			}
			return () => {
				try {
					toast(t("task.cta.comingSoon"));
				} catch {
					// non-blocking
				}
			};
		},
		[navigate, t],
	);

	const hasTrackedPageView = useRef(false);
	useEffect(() => {
		if (!hasTrackedPageView.current) {
			hasTrackedPageView.current = true;
			logGetReady("page_viewed");
		}
	}, [logGetReady]);

	// Deep-link focus: ?taskId=<uuid> → expand containing section, scroll + pulse.
	// Mirrors TrackPage's ?day= pattern: read once, clear immediately, auto-clear
	// state after ~1.6s so refresh does not replay.
	const [searchParams, setSearchParams] = useSearchParams();
	const taskIdParam = searchParams.get("taskId");

	// Null = no focused task. Non-null = ID of task currently pulsing.
	const [focusedTaskId, setFocusedTaskId] = useState<string | null>(null);
	// Null = no forced-open section. Non-null = stepKey whose section must open.
	const [forcedOpenStepKey, setForcedOpenStepKey] = useState<string | null>(
		null,
	);
	// Callback-ref map: task id → wrapper DOM element (populated as cards mount).
	const taskCardRefs = useRef<Map<string, HTMLElement>>(new Map());

	// Resolve journeyType from the user's profile. Derives role/status journeys
	// (e.g. surrogate → surrogacy_surrogate, currently_pregnant → pregnancy) that
	// skip the journey-types screen and leave fertilityJourneyTypes empty.
	const journeyType = useMemo(
		() => resolveJourneyType(userData?.details),
		[userData],
	);

	const {
		journey,
		stepStates,
		taskProgress,
		completedTaskIds,
		isLoading,
		error,
		mutations,
	} = useJourneyProgress(journeyType, { locale });

	// Resolve the current in-progress step for Ask Eve prefill context.
	const currentInProgressStep = useMemo(() => {
		if (!journey) return null;
		return (
			journey.steps.find(
				(s) => stepStates.get(s.id)?.status === "in_progress",
			) ?? null
		);
	}, [journey, stepStates]);

	// Unified Ask Eve handler: build prefill via the shared helper, write it to
	// ChatActionsContext (setPrefillQuestion), then navigate. No longer uses
	// navigate state.autoMessage so the prefill is reliably consumed by ChatPage.
	const buildOnAskEve = useCallback(
		(task: GetReadyTaskRenderModel) => {
			return () => {
				if (journey) {
					try {
						const prefill = buildAskEvePrefill({
							task: renderModelToJourneyTask(task),
							journey,
							step: currentInProgressStep,
							t: t as import("i18next").TFunction,
						});
						setPrefillQuestion(prefill);
					} catch {
						// non-blocking — still navigate even if prefill fails
					}
				}
				navigate("/chat");
			};
		},
		[journey, currentInProgressStep, setPrefillQuestion, navigate, t],
	);

	const buildOnAskEveForJourneyCard = useCallback(
		(task: GetReadyTaskRenderModel) => {
			const handler = buildOnAskEve(task);
			return (_journeyTask: JourneyTask) => handler();
		},
		[buildOnAskEve],
	);

	const buildOnChevronClick = useCallback(
		(task: GetReadyTaskRenderModel) => {
			const navigate_ = buildOnNavigate(task);
			return (_journeyTask: JourneyTask) => {
				if (isD2C && task.iconName !== "upload") {
					setInfoTaskId(task.id);
					return;
				}
				navigate_();
			};
		},
		[buildOnNavigate, isD2C],
	);

	const buildOnMarkComplete = useCallback(
		(task: GetReadyTaskRenderModel) => {
			if (task.kind === "custom") {
				return () => {
					try {
						logGetReady("task_mark_complete", {
							taskKey: task.taskKey,
						});
					} catch {
						// non-blocking
					}
					updateCustomTask(task.id, { status: "completed" })
						.then(() => {
							queryClient.invalidateQueries({ queryKey: ["customTasks"] });
							try {
								toast(t("task.toast.completed"));
							} catch {
								// non-blocking
							}
						})
						.catch(() => {
							try {
								toast(t("task.toast.completeError"));
							} catch {
								// non-blocking
							}
						});
				};
			}
			return () => {
				try {
					logGetReady("task_mark_complete", {
						taskKey: task.taskKey,
					});
				} catch {
					// non-blocking
				}
				mutations.completeTask.mutate(
					{ taskId: task.id, progressPercent: 100 },
					{
						onError: () => {
							try {
								toast(t("task.toast.completeError"));
							} catch {
								// non-blocking
							}
						},
						onSuccess: () => {
							try {
								toast(t("task.toast.completed"));
							} catch {
								// non-blocking
							}
						},
					},
				);
			};
		},
		[logGetReady, mutations.completeTask, queryClient, t],
	);

	const buildOnMarkIncomplete = useCallback(
		(task: GetReadyTaskRenderModel) => {
			if (task.kind === "custom") {
				return () => {
					try {
						logGetReady("task_mark_incomplete", {
							taskKey: task.taskKey,
						});
					} catch {
						// non-blocking
					}
					updateCustomTask(task.id, { status: "in_progress" })
						.then(() => {
							queryClient.invalidateQueries({ queryKey: ["customTasks"] });
							try {
								toast(t("task.toast.reopened"));
							} catch {
								// non-blocking
							}
						})
						.catch(() => {
							try {
								toast(t("task.toast.reopenError"));
							} catch {
								// non-blocking
							}
						});
				};
			}
			return () => {
				try {
					logGetReady("task_mark_incomplete", {
						taskKey: task.taskKey,
					});
				} catch {
					// non-blocking
				}
				mutations.reopenTask.mutate(
					{ taskId: task.id },
					{
						onError: () => {
							try {
								toast(t("task.toast.reopenError"));
							} catch {
								// non-blocking
							}
						},
						onSuccess: () => {
							try {
								toast(t("task.toast.reopened"));
							} catch {
								// non-blocking
							}
						},
					},
				);
			};
		},
		[logGetReady, mutations.reopenTask, queryClient, t],
	);

	const { data: rawCustomTasks = [] } = useQuery({
		queryKey: ["customTasks"],
		queryFn: getCustomTasks,
		retry: false,
		throwOnError: false,
	});

	const handleRetry = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["journeyProgress"] });
	}, [queryClient]);

	// Sub-step sections: tasks from the current (in_progress) step, grouped by
	// subStepId. An empty array means no in_progress step or no tasks.
	const subStepSections = useMemo(() => {
		return adaptJourneyProgressToSubStepSections({
			journey,
			stepStates,
			taskProgress,
			completedTaskIds,
			t,
		});
	}, [journey, stepStates, taskProgress, completedTaskIds, t]);

	// Custom tasks always render as a separate section at the end.
	const customTasksSection = useMemo(() => {
		if (rawCustomTasks.length === 0) return null;
		return {
			stepKey: "__custom__",
			stepName: t("steps.unknown.title"),
			tasks: rawCustomTasks.map((task, i) =>
				customTaskToRenderModel(task, i, locale),
			),
		};
	}, [rawCustomTasks, t, locale]);

	// Unified flat task list for deep-link resolution and allTasksCompleted check.
	const allSectionTasks = useMemo(() => {
		const subStepTasks = subStepSections.flatMap((s) => s.tasks);
		const customTasks = customTasksSection?.tasks ?? [];
		return [...subStepTasks, ...customTasks];
	}, [subStepSections, customTasksSection]);

	const infoIndex = allSectionTasks.findIndex((tk) => tk.id === infoTaskId);
	const infoTaskModel = infoIndex >= 0 ? allSectionTasks[infoIndex] : null;
	const infoTask = infoTaskModel
		? renderModelToJourneyTask(infoTaskModel)
		: null;

	const handleInfoMarkComplete = useCallback(() => {
		if (infoTaskModel) buildOnMarkComplete(infoTaskModel)?.();
		setInfoTaskId(null);
	}, [infoTaskModel, buildOnMarkComplete]);

	// Deep-link: resolve and consume the taskId param once sections are ready.
	// Guard against running while journey data is still loading — the param is
	// cleared on first run, so if sections are empty (loading) we'd lose the
	// taskId before we can match it against real data.
	useEffect(() => {
		if (!taskIdParam) return;
		if (isLoading) return;
		// Clear the param now that data is ready, so back-navigation won't re-focus.
		setSearchParams(
			(prev) => {
				const next = new URLSearchParams(prev);
				next.delete("taskId");
				return next;
			},
			{ replace: true },
		);
		// Check whether the task lives in a sub-step section.
		const matchingSubStep = subStepSections.find((section) =>
			section.tasks.some((task) => task.id === taskIdParam),
		);
		if (matchingSubStep) {
			setForcedOpenStepKey(matchingSubStep.subStepKey);
			setFocusedTaskId(taskIdParam);
		} else if (
			customTasksSection?.tasks.some((task) => task.id === taskIdParam)
		) {
			// Task lives in the custom section.
			setForcedOpenStepKey("__custom__");
			setFocusedTaskId(taskIdParam);
		} else {
			// Invalid taskId — silent fallback + observability breadcrumb.
			Sentry.addBreadcrumb({
				category: "navigation",
				message: "Invalid taskId on get-ready",
				level: "info",
				data: { rawTaskId: taskIdParam },
			});
		}
	}, [
		taskIdParam,
		isLoading,
		subStepSections,
		customTasksSection,
		setSearchParams,
	]);

	// Scroll the focused task card into view after its section has opened.
	// The section needs one render cycle to respond to forceOpen before the card
	// mounts into the ref map, so we defer the scroll with a short timeout.
	// Guard: scrollIntoView is not available in jsdom test environments.
	useEffect(() => {
		if (!focusedTaskId) return;
		const timer = setTimeout(() => {
			const node = taskCardRefs.current.get(focusedTaskId);
			if (!node) return;
			if (typeof node.scrollIntoView === "function") {
				node.scrollIntoView({ behavior: "smooth", block: "center" });
			}
		}, 150);
		return () => clearTimeout(timer);
	}, [focusedTaskId]);

	// Auto-clear focused state after ~1.6s (mirrors TrackPage's focusDayIso).
	useEffect(() => {
		if (!focusedTaskId) return;
		const timer = setTimeout(() => {
			setFocusedTaskId(null);
			setForcedOpenStepKey(null);
		}, 1600);
		return () => clearTimeout(timer);
	}, [focusedTaskId]);

	const handleOpenCreateModal = useCallback(() => {
		setModalState({ editing: null });
		try {
			logGetReady("custom_task_create_opened");
		} catch {
			// non-blocking
		}
	}, [logGetReady]);

	const handleCloseModal = useCallback(() => setModalState(null), []);

	const handleTaskSaved = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["customTasks"] });
	}, [queryClient]);

	const handleEditCustomTask = useCallback(
		(taskId: string) => {
			const instance = rawCustomTasks.find((rt) => rt.id === taskId);
			if (instance) setModalState({ editing: instance });
		},
		[rawCustomTasks],
	);

	const handleDeleteCustomTask = useCallback(
		(taskId: string) => {
			if (!window.confirm(t("customTask.confirmDelete"))) return;
			deleteCustomTask(taskId)
				.then(() => {
					queryClient.invalidateQueries({ queryKey: ["customTasks"] });
					try {
						logGetReady("custom_task_deleted");
					} catch {
						// non-blocking
					}
					try {
						toast(t("customTask.toast.deleted"));
					} catch {
						// non-blocking
					}
				})
				.catch(() => {
					try {
						toast(t("customTask.toast.error"));
					} catch {
						// non-blocking
					}
				});
		},
		[logGetReady, queryClient, t],
	);

	const allTasksCompleted =
		allSectionTasks.length > 0 &&
		allSectionTasks.every((task) => task.status === "completed");

	const isCompleted = !isLoading && !error && allTasksCompleted;
	const userName = getUserDisplayName(userData);

	// Filter sub-step sections by active tab (preserves all sections, only filters tasks).
	const filteredSubStepSections = useMemo(() => {
		return subStepSections.map((section) => ({
			...section,
			tasks: section.tasks.filter((task) =>
				activeTab === "completed"
					? task.status === "completed"
					: task.status !== "completed",
			),
		}));
	}, [subStepSections, activeTab]);

	// Filter custom tasks by active tab.
	const filteredCustomSection = useMemo(() => {
		if (!customTasksSection) return null;
		const tasks = customTasksSection.tasks.filter((task) =>
			activeTab === "completed"
				? task.status === "completed"
				: task.status !== "completed",
		);
		return { ...customTasksSection, tasks };
	}, [customTasksSection, activeTab]);

	// Current step name for the page header subtitle. Derived directly from the
	// first in_progress step in stepStates (not from subStepSections which has
	// already narrowed to that step's tasks).
	const currentStepName = useMemo(() => {
		if (!journey) return null;
		const sorted = journey.steps.slice().sort((a, b) => a.order - b.order);
		const inProgressStep = sorted.find((step) => {
			const state = stepStates.get(step.id);
			return state?.status === "in_progress";
		});
		return inProgressStep?.title ?? null;
	}, [journey, stepStates]);

	// View toggle threads through to each card: "list" = minimal (bottom action
	// row hidden), "grid" = expanded (full card).
	const cardView: TaskViewValue = viewToggle;

	const hasTasksInTab =
		filteredSubStepSections.some((s) => s.tasks.length > 0) ||
		(filteredCustomSection !== null && filteredCustomSection.tasks.length > 0);

	// Journey not available: user has a journeyType we couldn't fetch a CDN
	// journey for, or no journeyType at all and loading is done.
	const showNotAvailable =
		!isLoading && !error && journey === null && !isCompleted;

	return (
		<div className={styles.wrapper}>
			<Header />
			<main className={styles.main}>
				{/* Page header: dynamic based on completion state */}
				<div className={styles.pageHeader}>
					<img
						src="/images/home/insights/prepare-insights.png"
						alt=""
						className={styles.pageIcon}
					/>
					<div className={styles.pageHeaderText}>
						<h1 className={styles.pageTitle}>
							{isCompleted
								? t("empty.title", { name: userName })
								: t("page.title")}
						</h1>
						{isCompleted ? (
							<p className={styles.pageSubtitle}>{t("empty.subtitle")}</p>
						) : currentStepName ? (
							<p className={styles.pageSubtitle}>
								{t("page.subtitle", { stepName: currentStepName })}
							</p>
						) : null}
					</div>
				</div>

				{/* Content states */}
				{isLoading && <LoadingSkeleton />}
				{error && <ErrorState onRetry={handleRetry} t={t} />}
				{isCompleted && <EducationHubExplore />}
				{showNotAvailable && <JourneyNotAvailable />}

				{!isLoading && !error && !isCompleted && !showNotAvailable && (
					<>
						{/* Controls row: tabs + view toggle — sits above the grey card */}
						<div className={styles.controlsRow}>
							<TaskTabsToggle
								value={activeTab}
								onChange={handleTabChange}
								ongoingLabel={t("controls.tabs.ongoing")}
								completedLabel={t("controls.tabs.completed")}
							/>
							<TaskViewToggle
								value={viewToggle}
								onChange={handleViewChange}
								listLabel={t("controls.view.collapsed")}
								gridLabel={t("controls.view.expanded")}
							/>
						</div>

						<div className={styles.tasksContainer}>
							{/* Title row: section title + add task button — under the tabs */}
							<div className={styles.sectionTitleRow}>
								<h2 className={styles.sectionTitle}>{t("section.title")}</h2>
								<button
									type="button"
									className={styles.addTaskButton}
									onClick={handleOpenCreateModal}
									aria-label={t("customTask.actions.add")}
								>
									<span className={styles.addTaskIcon}>+</span>
								</button>
							</div>

							{/* Task list — grouped by sub-step sections within the current
						    in_progress step. The first sub-step is open by default.
						    Custom tasks are appended as a separate section at the end. */}
							{hasTasksInTab ? (
								<div className={styles.sectionsList}>
									{filteredSubStepSections.map((section, index) => {
										const displayTitle =
											section.subStepKey === "__ungrouped__"
												? t("subStepSection.ungrouped.title")
												: section.subStepTitle;
										return (
											<GetReadySubStepSection
												key={section.subStepKey}
												subStepTitle={displayTitle}
												completedCount={section.completedCount}
												totalCount={section.totalCount}
												defaultOpen={index === 0}
												forceOpen={forcedOpenStepKey === section.subStepKey}
												expandLabel={t("subStepSection.expand", {
													subStepTitle: displayTitle,
												})}
												collapseLabel={t("subStepSection.collapse", {
													subStepTitle: displayTitle,
												})}
												progressLabel={t("subStepSection.progressLabel", {
													completed: section.completedCount,
													total: section.totalCount,
												})}
												hideProgress={isD2C}
											>
												{section.tasks.map((task) => {
													const journeyTask = renderModelToJourneyTask(task);
													const isFocused = focusedTaskId === task.id;
													return (
														<div
															key={task.id}
															ref={(el) => {
																if (el) {
																	taskCardRefs.current.set(task.id, el);
																} else {
																	taskCardRefs.current.delete(task.id);
																}
															}}
															className={[
																styles.taskCardWrapper,
																isFocused ? styles.focused : "",
															]
																.filter(Boolean)
																.join(" ")}
															data-task-id={task.id}
														>
															<TaskCard
																task={journeyTask}
																view={cardView}
																fullWidth
																hideProgress={isD2C}
																onAskEve={buildOnAskEveForJourneyCard(task)}
																onChevronClick={buildOnChevronClick(task)}
																askEveLabel={t("task.actions.askEve")}
																openLabel={t("task.action.open", {
																	title: journeyTask.title,
																})}
																progressLabel={(count, total, unit) =>
																	t("task.progress.label", {
																		current: count,
																		total,
																		unit,
																	})
																}
																onMarkComplete={buildOnMarkComplete(task)}
																onMarkIncomplete={buildOnMarkIncomplete(task)}
																moreOptionsLabel={t(
																	"task.actions.moreOptions",
																	{
																		title: journeyTask.title,
																	},
																)}
																markCompleteLabel={t(
																	"task.actions.markComplete",
																)}
																markIncompleteLabel={t(
																	"task.actions.markIncomplete",
																)}
																completedLabel={t("task.completed")}
																completedOnTimeLabel={t("task.completedOnTime")}
															/>
														</div>
													);
												})}
											</GetReadySubStepSection>
										);
									})}
									{filteredCustomSection &&
										filteredCustomSection.tasks.length > 0 && (
											<GetReadyStepSection
												key="__custom__"
												stepName={filteredCustomSection.stepName}
												stepStatus="not_started"
												stepOrder={0}
												totalSteps={0}
												defaultOpen={false}
												forceOpen={forcedOpenStepKey === "__custom__"}
												expandLabel={t("stepSection.expand", {
													stepName: filteredCustomSection.stepName,
												})}
												collapseLabel={t("stepSection.collapse", {
													stepName: filteredCustomSection.stepName,
												})}
												progressLabel={t("stepSection.progressLabel", {
													current: 0,
													total: 0,
												})}
												taskCount={filteredCustomSection.tasks.length}
												hideProgress={isD2C}
											>
												{filteredCustomSection.tasks.map((task) => {
													const journeyTask = renderModelToJourneyTask(task);
													const isFocused = focusedTaskId === task.id;
													return (
														<div
															key={task.id}
															ref={(el) => {
																if (el) {
																	taskCardRefs.current.set(task.id, el);
																} else {
																	taskCardRefs.current.delete(task.id);
																}
															}}
															className={[
																styles.taskCardWrapper,
																isFocused ? styles.focused : "",
															]
																.filter(Boolean)
																.join(" ")}
															data-task-id={task.id}
														>
															<TaskCard
																task={journeyTask}
																view={cardView}
																fullWidth
																hideProgress={isD2C}
																onAskEve={buildOnAskEveForJourneyCard(task)}
																onChevronClick={buildOnChevronClick(task)}
																askEveLabel={t("task.actions.askEve")}
																openLabel={t("task.action.open", {
																	title: journeyTask.title,
																})}
																progressLabel={(count, total, unit) =>
																	t("task.progress.label", {
																		current: count,
																		total,
																		unit,
																	})
																}
																onMarkComplete={buildOnMarkComplete(task)}
																onMarkIncomplete={buildOnMarkIncomplete(task)}
																onEdit={() => handleEditCustomTask(task.id)}
																onDelete={() => handleDeleteCustomTask(task.id)}
																editLabel={t("task.actions.edit")}
																deleteLabel={t("task.actions.delete")}
																moreOptionsLabel={t(
																	"task.actions.moreOptions",
																	{
																		title: journeyTask.title,
																	},
																)}
																markCompleteLabel={t(
																	"task.actions.markComplete",
																)}
																markIncompleteLabel={t(
																	"task.actions.markIncomplete",
																)}
																completedLabel={t("task.completed")}
																completedOnTimeLabel={t("task.completedOnTime")}
															/>
														</div>
													);
												})}
											</GetReadyStepSection>
										)}
								</div>
							) : (
								<p className={styles.emptyTabMessage}>
									{t("empty.completedTab")}
								</p>
							)}
						</div>
					</>
				)}
			</main>

			<CustomTaskModal
				isOpen={modalState !== null}
				onClose={handleCloseModal}
				editingTask={modalState?.editing ?? null}
				onSaved={handleTaskSaved}
			/>
			<JourneyTaskDetailSheet
				isOpen={infoTask !== null}
				task={infoTask}
				onClose={() => setInfoTaskId(null)}
				canPrev={infoIndex > 0}
				canNext={infoIndex >= 0 && infoIndex < allSectionTasks.length - 1}
				onPrev={() => setInfoTaskId(allSectionTasks[infoIndex - 1]?.id ?? null)}
				onNext={() => setInfoTaskId(allSectionTasks[infoIndex + 1]?.id ?? null)}
				onMarkComplete={handleInfoMarkComplete}
				markCompleteLabel={t("task.actions.markComplete")}
			/>
		</div>
	);
}
